import { DragDropContext, Draggable, DraggableProvidedDraggableProps, Droppable } from '@hello-pangea/dnd';
import { Card, Divider, Group, rem, Stack, Text } from '@mantine/core';
import { IconGripVertical, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import cx from 'clsx';
import { DialogSize } from '../../../../../react-commons/components/data-display/dialog/ResizableDialog';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { useMyForm } from '../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import { useContentSectionContentActions } from '../../../resource/resources/artifacts/content/contentSectionContentResource';
import {
    ContentSectionMutationResourceSchema,
    ContentSectionPropertyValidation,
    defaultContentSectionResource,
    useContentSectionActions,
} from '../../../resource/resources/artifacts/content/contentSectionResource';
import { TOOLTIPS } from '../../../resource/tooltipTexts';
import { PropertyInputs } from '../../reusable-components/base/property-bases/PropertyInputs';
import { EditChildBaseDialog } from '../../reusable-components/dialogs/EditChildBaseDialog';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { DisableableIconButtonWithTooltip } from '../../reusable-components/DisableableIconButtonWithTooltip';
import { Form_EditableDisplayBar } from '../../reusable-components/form-components/Form_EdiableDisplayBar';
import classes from '../../util/DndListHandle.module.css';
import { ContentSectionContentCreationDialog } from './ContentSectionContentCreationDialog';
import { ContentSectionContentDisplay } from './ContentSectionContentDisplay';
import { ContentSectionContentEditDialog } from './ContentSectionContentEditDialog';

export const ContentSectionEditDialog = ({
    onClose,
    contentSectionId,
    versionId,
}: {
    onClose: () => void;
    contentSectionId: string;
    versionId: string;
}) => {
    const nestedDialog = useNestedDialogFunction();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const contentSectionActions = useContentSectionActions();
    const contentSectionContentActions = useContentSectionContentActions();

    const contentSectionQuery = contentSectionActions.useGetResource(contentSectionId);
    const updateContentSection = contentSectionActions.useUpdateResource();

    const contentSectionContentQuery = contentSectionContentActions.useGetAllResources(contentSectionId);

    const reorderContentSectionContent = contentSectionContentActions.useChangeIndexOfResource();
    const deleteContentSectionContent = contentSectionContentActions.useDeleteResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: ContentSectionMutationResourceSchema,
        defaultValues: defaultContentSectionResource,
        submitHandler: async (data) => {
            await updateContentSection.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: contentSectionQuery.data,
    });

    const getWidthPercentage = (weight: number) => {
        const weightSum =
            contentSectionContentQuery.data
                ?.map((value) => value.decorator.weight)
                .reduce((partialSum, a) => partialSum + a, 0) ?? 1;
        const percentage = (weight / weightSum) * 100;
        return `${percentage}%`;
    };

    const onAdd = () => {
        nestedDialog.showNestedDialog(
            <ContentSectionContentCreationDialog
                onClose={nestedDialog.closeNestedDialog}
                parentId={contentSectionId}
                versionId={versionId}
            />
        );
    };

    const onEdit = (id: string) => {
        nestedDialog.showNestedDialog(
            <ContentSectionContentEditDialog onClose={nestedDialog.closeNestedDialog} id={id} versionId={versionId} />
        );
    };

    const onDelete = (id: string | undefined) => {
        if (id) {
            deleteResourceDialogFunction.deleteResource(
                id,
                null,
                ResourceType.CONTENT_SECTION_CONTENT,
                deleteContentSectionContent
            );
        }
    };

    const getLeftPositionOfDraggable = (draggableStyle: DraggableProvidedDraggableProps) => {
        if (draggableStyle.style && 'left' in draggableStyle.style) {
            return draggableStyle.style.left - 350;
        }
        return undefined;
    };

    const draggableContentSectionCards = contentSectionContentQuery.data?.map((section, index) => (
        <Draggable draggableId={section.id} index={index} key={section.id}>
            {(provided, snapshot) => (
                <Card
                    withBorder
                    radius={'md'}
                    w={getWidthPercentage(section.decorator.weight)}
                    className={cx({ [classes.itemHorizonalDragging]: snapshot.isDragging })}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    style={{
                        ...provided.draggableProps.style,
                        left: getLeftPositionOfDraggable(provided.draggableProps),
                    }}
                >
                    <Card.Section withBorder inheritPadding py="xs">
                        <div style={{ display: 'inline-flex', width: '100%', alignItems: 'center' }}>
                            <div {...provided.dragHandleProps} className={classes.dragHandle}>
                                <IconGripVertical style={{ width: rem(18), height: rem(18) }} stroke={1.5} />
                            </div>
                            <Stack align={'flex-start'} justify={'flex-start'} w={'100%'}>
                                <Group w={'100%'} wrap={'nowrap'} style={{ alignItems: 'start' }}>
                                    <div style={{ width: '93%' }}>
                                        <ContentSectionContentDisplay contentSectionContentId={section.id} />
                                    </div>
                                    <div style={{ flexGrow: 1 }} />
                                    <Group justify={'flex-start'}>
                                        <DisableableIconButtonWithTooltip
                                            disabled={false}
                                            tooltipText={TOOLTIPS.EDIT}
                                            icon={<IconPencil />}
                                            onClick={() => onEdit(section.id)}
                                        />
                                        <DisableableIconButtonWithTooltip
                                            disabled={false}
                                            tooltipText={TOOLTIPS.DELETE}
                                            icon={<IconTrash />}
                                            onClick={() => onDelete(section.id)}
                                        />
                                    </Group>
                                </Group>
                            </Stack>
                        </div>
                    </Card.Section>
                </Card>
            )}
        </Draggable>
    ));

    const dragAndDrop = () => {
        return (
            <DragDropContext
                onDragEnd={({ destination, source }) => {
                    const resource = contentSectionContentQuery.data?.at(source.index);
                    const destinationIndex = destination?.index;
                    if (resource && destinationIndex !== undefined) {
                        reorderContentSectionContent.mutateAsync({
                            resource,
                            index: destinationIndex,
                            parentId: contentSectionId,
                        });
                    }
                }}
            >
                <Droppable droppableId={'dnd-row'} direction={'horizontal'}>
                    {(provided) => (
                        <Group
                            wrap={'nowrap'}
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{ width: '100%' }}
                        >
                            {draggableContentSectionCards}
                            {provided.placeholder}
                        </Group>
                    )}
                </Droppable>
            </DragDropContext>
        );
    };

    return (
        <EditChildBaseDialog
            onClose={onClose}
            edited={false}
            title={'Edit Content Section'}
            resourceType={[ResourceType.CONTENT_SECTION, ResourceType.CONTENT_SECTION_CONTENT]}
            dialogSize={DialogSize.MEDIUM_LARGE}
            customButtons={
                <DisableableIconButtonWithTooltip
                    disabled={false}
                    tooltipText={'Add Section Content'}
                    icon={<IconPlus />}
                    onClick={onAdd}
                />
            }
        >
            <Stack gap={5}>
                <QueryBaseComponent name="ContentSection" query={contentSectionQuery}>
                    <form onSubmit={onSubmit}>
                        <Stack>
                            <PropertyInputs.String
                                name={formNames.title}
                                control={form.control}
                                nullable={true}
                                readOnly={false}
                                propertyValidationInformation={{
                                    resourceId: contentSectionId,
                                    resourceType: ResourceType.CONTENT_SECTION,
                                    propertyIdentifier: ContentSectionPropertyValidation.TITLE,
                                }}
                            />
                            <Group justify="flex-end">
                                <Form_EditableDisplayBar form={form} canSubmitDefaultValue={false} />
                            </Group>
                        </Stack>
                    </form>
                </QueryBaseComponent>
                <Divider />
                <div id={'content_section_edit_dialog'}>
                    <QueryBaseComponent
                        name="ContentSectionContent"
                        query={contentSectionContentQuery}
                        NullQueryContent={
                            <Group grow>
                                <Text>No Section Content Available</Text>
                                <Group justify={'flex-end'}>
                                    <DisableableIconButtonWithTooltip
                                        disabled={false}
                                        tooltipText={'Add Section Content'}
                                        icon={<IconPlus />}
                                        onClick={onAdd}
                                    />
                                </Group>
                            </Group>
                        }
                    >
                        {dragAndDrop()}
                    </QueryBaseComponent>
                </div>
            </Stack>
        </EditChildBaseDialog>
    );
};
